import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from "react-redux";
import axios from 'axios';
import Message from './Message';
import CallLogMessage from './CallLogMessage';
import { toast } from "react-toastify";
import ClipLoader from "react-spinners/ClipLoader";
import Typing from './Typing';
import FileMessage from "./FileMessage";
import { msgDateHandler } from "../../utils/date";
import {
    deleteMessage,
    updateMessagesAndConversations,
    deleteFile,
} from "../../redux/features/chat/chatSlice";
import SocketContext from "../../context/SocketContext";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

const ChatMessages = ({ typing, searchQuery, socket }) => {

    const { messages, activeConversation } = useSelector((state) => state.chat);
    const { user } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const endRef = useRef();
    const firstHighlightRef = useRef();
    const messagesContainerRef = useRef();
    const [highlightedMessages, setHighlightedMessages] = useState([]);
    const [currentHighlightIndex, setCurrentHighlightIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [callLogs, setCallLogs] = useState([]);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [skip, setSkip] = useState(0);
    const limit = 30;

    // Reset pagination state when conversation changes
    useEffect(() => {
        setSkip(0);
        setHasMore(true);
    }, [activeConversation?._id]);

    // Fetch call logs when conversation changes
    useEffect(() => {
        console.log('🔍 Call log fetch effect triggered', {
            conversationId: activeConversation?._id,
            backendUrl: process.env.REACT_APP_BACKEND_URL
        });

        if (activeConversation?._id) {
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/calllogs/${activeConversation._id}`;
            console.log('📡 Fetching call logs from:', url);

            // Use withCredentials: true to send cookies (since backend uses cookies for auth)
            axios.get(url, { withCredentials: true })
                .then(res => {
                    console.log('✅ Call logs response:', res.data);
                    setCallLogs(res.data || []);
                    console.log('📞 Loaded call logs:', res.data?.length || 0);
                })
                .catch(err => {
                    console.error('❌ Failed to fetch call logs:', err);
                    console.error('Error details:', err.response?.data || err.message);
                });
        } else {
            console.log('⚠️ Skipping call log fetch - missing conversation');
        }
    }, [activeConversation]);

    // Infinite scroll handler
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            if (container.scrollTop === 0 && !loadingMore && hasMore) {
                loadMoreMessages();
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [loadingMore, hasMore, skip, activeConversation]);

    const loadMoreMessages = async () => {
        if (!activeConversation?._id || loadingMore || !hasMore) return;

        setLoadingMore(true);
        const previousScrollHeight = messagesContainerRef.current?.scrollHeight || 0;

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/message/${activeConversation._id}?limit=${limit}&skip=${skip + limit}`,
                { withCredentials: true }
            );

            if (response.data.length < limit) {
                setHasMore(false);
            }

            if (response.data.length > 0) {
                // Prepend older messages
                dispatch({ type: 'chat/prependMessages', payload: response.data });
                setSkip(skip + limit);

                // Maintain scroll position
                setTimeout(() => {
                    if (messagesContainerRef.current) {
                        const newScrollHeight = messagesContainerRef.current.scrollHeight;
                        messagesContainerRef.current.scrollTop = newScrollHeight - previousScrollHeight;
                    }
                }, 0);
            }
        } catch (error) {
            console.error('Failed to load more messages:', error);
        } finally {
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, typing, callLogs]);

    // useEffect(() => {
    //     setFilteredMessages(
    //         messages.filter(message => 
    //             message.message.toLowerCase().includes(searchQuery.toLowerCase())
    //         )
    //     );
    // }, [searchQuery, messages]);

    // useEffect(() => {
    //     if (searchQuery) {
    //         if (firstHighlightRef.current) {
    //             firstHighlightRef.current.scrollIntoView({ behavior: "smooth" });
    //         }
    //     } else {
    //         scrollToBottom();
    //     }
    // }, [searchQuery]);

    useEffect(() => {
        if (searchQuery) {
            const highlightRefs = [];
            messages.forEach((message, index) => {
                if (message.message.toLowerCase().includes(searchQuery.toLowerCase())) {
                    highlightRefs.push(index);
                }
            });
            setHighlightedMessages(highlightRefs);
            setCurrentHighlightIndex(0);
        } else {
            setHighlightedMessages([]);
            scrollToBottom();
        }
    }, [searchQuery, messages]);

    useEffect(() => {
        if (highlightedMessages.length > 0 && currentHighlightIndex < highlightedMessages.length) {
            const messageIndex = highlightedMessages[currentHighlightIndex];
            const messageElement = document.getElementById(messages[messageIndex]._id);
            if (messageElement) {
                messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [currentHighlightIndex, highlightedMessages, messages]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Enter' && searchQuery) {
                setCurrentHighlightIndex((prevIndex) => (prevIndex + 1) % highlightedMessages.length);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [searchQuery, highlightedMessages]);

    const escapeRegExp = (string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    const linkify = (text) => {
        const urlPattern = /(https?:\/\/[^\s]+)/g;
        const phonePattern = /(\b\d{3}[-.]?\d{3}[-.]?\d{4}\b)/g;

        return text.split(/(\s+)/).map((part, index) => {
            if (urlPattern.test(part)) {
                return <a key={index} href={part} target="_blank" rel="noopener noreferrer">{part}</a>;
            } else if (phonePattern.test(part)) {
                return <a key={index} href={`tel:${part}`}>{part}</a>;
            }
            return part;
        });
    };

    const highlightSearchTerm = (text, term) => {
        if (!term) return linkify(text);

        const escapedTerm = escapeRegExp(term);
        const parts = text.split(new RegExp(`(${escapedTerm})`, 'gi'));
        let firstHighlightFound = false;

        return (
            <>
                {parts.map((part, index) => {
                    const linkedPart = linkify(part);
                    if (part.toLowerCase() === term.toLowerCase()) {
                        if (!firstHighlightFound) {
                            firstHighlightFound = true;
                            return (
                                <span key={index} ref={firstHighlightRef} style={{ backgroundColor: 'darkgoldenrod', display: 'inline-block' }}>
                                    {linkedPart}
                                </span>
                            );
                        } else {
                            return (
                                <span key={index} style={{ backgroundColor: 'darkgoldenrod', display: 'inline-block' }}>
                                    {linkedPart}
                                </span>
                            );
                        }
                    }
                    return linkedPart;
                })}
            </>
        );
    };

    const scrollToBottom = () => {
        endRef.current.scrollIntoView({ behavior: "smooth" });
    };

    const [windowHeight, setWindowHeight] = useState({ winHeight: window.innerHeight })

    const detectSize = () => {
        setWindowHeight({
            winHeight: window.innerHeight
        })
    }

    useEffect(() => {
        window.addEventListener('resize', detectSize);
        return () => {
            window.removeEventListener('resize', detectSize)
        }
    }, [windowHeight])

    const currentTime = new Date().toLocaleTimeString();

    useEffect(() => {
        socket.on('messageDeleted', (messageId) => {
            dispatch(deleteMessage.fulfilled(messageId));
        });

        return () => {
            socket.off('messageDeleted');
        };
    }, [dispatch]);

    useEffect(() => {
        const handleFileDeleted = ({ messageId, fileUrl }) => {
            dispatch(deleteFile.fulfilled({ messageId, fileUrl }));
        };

        socket.on('fileDeleted', handleFileDeleted);

        return () => {
            socket.off('fileDeleted', handleFileDeleted);
        };
    }, [dispatch]);

    const removeMessage = async (id) => {
        socket.emit('deleteMessage', id);
        await dispatch(deleteMessage(id));
        // const messageElement = document.getElementById(id);
        // if (messageElement) {
        //     messageElement.style.display = 'none';
        // }
    };

    const removeFile = async (messageId, fileUrl) => {
        socket.emit('deleteFile', { messageId, fileUrl });
        await dispatch(deleteFile({ messageId, fileUrl }));
    };

    const confirmDelete = (id) => {
        confirmAlert({
            title: `Message`,
            message: "Are you sure to do delete this message?",
            buttons: [
                {
                    label: "Delete",
                    onClick: () => removeMessage(id),
                },
                {
                    label: "Cancel",
                    // onClick: () => alert("Click No"),
                },
            ],
        });
    };

    const confirmDeleteFile = (messageId, fileUrl) => {
        confirmAlert({
            title: `Message`,
            message: "Are you sure to do delete this file?",
            buttons: [
                {
                    label: "Delete",
                    onClick: () => removeFile(messageId, fileUrl),
                },
                {
                    label: "Cancel",
                    // onClick: () => alert("Click No"),
                },
            ],
        });
    };

    return (
        <>
            <div className="messageBg">
                <div className="messageScroll" ref={messagesContainerRef} style={{ height: windowHeight.winHeight - 100 }}>
                    {loadingMore && (
                        <div style={{ textAlign: 'center', padding: '10px' }}>
                            <ClipLoader size={20} />
                        </div>
                    )}
                    {/*loading ? (
                <div className="customSpinner" style={{ height: windowHeight.winHeight - 150 }}>
                    <ClipLoader />
                </div>
                ) : (*/}
                    {
                        // Merge messages and call logs, sort by timestamp
                        [...messages, ...callLogs.map(log => ({ ...log, isCallLog: true }))]
                            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                            .map((item, index, allItems) => {
                                if (item.isCallLog) {
                                    // Render call log
                                    return <CallLogMessage key={item._id} callLog={item} user={user} />;
                                }

                                // Render regular message
                                const message = item;
                                const previousItem = allItems[index - 1];
                                const showDateSeparator = !previousItem || msgDateHandler(previousItem.createdAt) !== msgDateHandler(message.createdAt);

                                return (
                                    <div key={message._id}>
                                        {showDateSeparator && (
                                            <div className="date-separator">
                                                {msgDateHandler(message.createdAt)}
                                            </div>
                                        )}
                                        <div className="position-relative" style={{ margin: '10px 0' }} id={message._id}>
                                            {message.files.length > 0
                                                ? message.files.map((file, i) => (
                                                    <FileMessage
                                                        FileMessage={file}
                                                        message={message}
                                                        key={i}
                                                        me={user._id === message.sender._id}
                                                        confirmDeleteFile={confirmDeleteFile}
                                                        highlightSearchTerm={highlightSearchTerm}
                                                        searchQuery={searchQuery}
                                                    />
                                                ))
                                                : null}
                                            {message.message.length > 0 ? (
                                                <Message
                                                    message={message}
                                                    key={message._id}
                                                    me={user._id === message.sender._id}
                                                    linkify={linkify}
                                                    confirmDelete={confirmDelete}
                                                    highlightSearchTerm={highlightSearchTerm}
                                                    searchQuery={searchQuery}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                );
                            })
                    }
                    {typing && <Typing />}
                    <div className="mt-2" ref={endRef}></div>
                </div>
            </div>
        </>
    );
}


const ChatMessagesWithContext = (props) => (
    <SocketContext.Consumer>
        {(socket) => <ChatMessages {...props} socket={socket} />}
    </SocketContext.Consumer>
);
export default ChatMessagesWithContext;